import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export type FontSize = 'sm' | 'md' | 'lg';
const STORAGE_KEY = 'iq.font-size';

const SIZE_PX: Record<FontSize, string> = {
  sm: '14px',
  md: '16px',
  lg: '18px',
};

interface FontSizeValue {
  size: FontSize;
  setSize: (s: FontSize) => void;
}

const FontSizeContext = createContext<FontSizeValue | null>(null);

function read(): FontSize {
  if (typeof window === 'undefined') return 'md';
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v === 'sm' || v === 'md' || v === 'lg' ? v : 'md';
}

function apply(size: FontSize): void {
  if (typeof document === 'undefined') return;
  document.documentElement.style.fontSize = SIZE_PX[size];
}

export function FontSizeProvider({ children }: { children: ReactNode }) {
  const [size, setSizeState] = useState<FontSize>(() => read());
  useEffect(() => apply(size), [size]);
  const setSize = useCallback((s: FontSize) => {
    window.localStorage.setItem(STORAGE_KEY, s);
    setSizeState(s);
  }, []);
  const value = useMemo<FontSizeValue>(() => ({ size, setSize }), [size, setSize]);
  return <FontSizeContext.Provider value={value}>{children}</FontSizeContext.Provider>;
}

export function useFontSize(): FontSizeValue {
  const ctx = useContext(FontSizeContext);
  if (!ctx) throw new Error('useFontSize must be used inside <FontSizeProvider>');
  return ctx;
}
